// imports
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { FC, memo, useEffect, useState } from 'react';

// components
import { LoadingButton } from './LoadingButton';
import CountdownTimer from './Timer';

// interfaces, constants, helper
import { ARE_YOU_SURE_TEXT, CANCEL_TEXT, CONFIRM_TEXT, PROCESSING_TEXT } from 'constants/constant';
import { ConsentDialogProps } from 'interfaces';
import { borderTop } from 'theme/styleConstant';

export const ConsentDialog: FC<ConsentDialogProps> = memo(
  ({
    open,
    onClose,
    headerText,
    onSuccess,
    loading,
    successText = CONFIRM_TEXT,
    cancelText = CANCEL_TEXT,
    isError,
    children,
    countDown,
    onCountDownComplete,
  }) => {
    const [disableBtn, setDisableBtn] = useState(false);
    const [startTimer, setStartTimer] = useState(false);

    const handleClose = () => {
      if (!loading) {
        onClose();
      }
    };

    const handleConfirm = () => {
      if (countDown) {
        setStartTimer(true);
      } else {
        onSuccess?.();
      }
    };

    const handleCountDownComplete = () => {
      setDisableBtn(true);
      onCountDownComplete?.();
    };

    useEffect(() => {
      if (!open) {
        setDisableBtn(false);
        setStartTimer(false);
      }
    }, [open]);

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="consent-dialog-title"
        aria-describedby="consent-dialog-description"
        maxWidth="md"
        fullWidth>
        <DialogTitle id="consent-dialog-title" textTransform="uppercase">
          {headerText}
        </DialogTitle>
        <DialogContent id="consent-dialog-description" {...borderTop}>
          {<Box>{children}</Box>}
          {!startTimer && (
            <Typography variant="subtitle1" mt={1}>
              {ARE_YOU_SURE_TEXT}
            </Typography>
          )}
          {onCountDownComplete && (
            <CountdownTimer onComplete={handleCountDownComplete} startTimer={startTimer} />
          )}
        </DialogContent>
        <DialogActions {...borderTop}>
          <Button onClick={handleClose} disabled={disableBtn} color="primary" variant="contained">
            {cancelText}
          </Button>
          {!startTimer && (
            <LoadingButton
              onClick={handleConfirm}
              loading={loading}
              color={isError ? 'error' : 'success'}
              disabled={loading}
              text={successText}
              loadingText={PROCESSING_TEXT}
            />
          )}
        </DialogActions>
      </Dialog>
    );
  },
);
